import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, ArrowRight, GraduationCap } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1702538358013-61ed978425b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3OTM3OTYzMXww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error);
      return;
    }
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white" data-testid="login-page">
      {/* Left — form */}
      <div className="flex flex-col px-8 sm:px-12 lg:px-20 py-10">
        <div className="flex items-center gap-3 mb-16">
          <div className="w-9 h-9 rounded-sm bg-[#0f172a] flex items-center justify-center">
            <GraduationCap size={18} className="text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-[15px] text-slate-900">Rattrapage Builder</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
              École d'Ingénieurs
            </div>
          </div>
        </div>

        <div className="max-w-md w-full my-auto">
          <div className="label-xs mb-3">Accès Staff</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]">
            Concevez vos rattrapages
            <br />
            <span className="text-[#2563eb]">en toute autonomie.</span>
          </h1>
          <p className="text-slate-600 mt-5 text-[15px] leading-relaxed">
            Générez des QCM de 4 questions calibrés sur 1h, fidèles à vos cours et syllabus,
            prêts à être déposés sur Microsoft Forms.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="label-xs block mb-2">Adresse e-mail</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="prenom.nom@ecole.fr"
                data-testid="login-email-input"
                className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-sm bg-white focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
              />
            </div>
            <div>
              <label className="label-xs block mb-2">Mot de passe</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-sm bg-white focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
              />
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="group w-full flex items-center justify-center gap-2 bg-[#0f172a] text-white px-4 py-3 rounded-sm font-semibold text-sm hover:bg-slate-800 transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <>Se connecter <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" /></>}
            </button>

            <div className="text-sm text-slate-600 pt-2">
              Pas encore de compte ?{" "}
              <Link to="/register" data-testid="goto-register" className="text-[#2563eb] font-semibold hover:underline">
                Créer un accès staff
              </Link>
            </div>
          </form>
        </div>

        <div className="mt-12 text-xs text-slate-400">
          © {new Date().getFullYear()} — Outil interne de génération de rattrapages.
        </div>
      </div>

      {/* Right — image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Architecture minimaliste"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="label-xs text-white/70 mb-2">Méthode</div>
          <div className="font-display text-2xl font-semibold leading-snug max-w-md">
            « 4 problèmes · 15 minutes par question · 1 heure de raisonnement rigoureux. »
          </div>
        </div>
      </div>
    </div>
  );
}
